import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Heart,
  Reply,
  Send,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Calendar,
  RefreshCw
} from 'lucide-react';
import commentService, { Comment, CommentFormData } from '../services/commentService';

interface CommentSectionProps {
  contentType: 'blog' | 'case_study';
  contentId: number;
  title?: string;
}

interface CommentItemProps {
  comment: Comment;
  onReply: (parentId: number) => void;
  onLike: (commentId: number) => void;
  level?: number;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, onReply, onLike, level = 0 }) => {
  const [showReplies, setShowReplies] = useState(true);
  const [isLiking, setIsLiking] = useState(false);

  const handleLike = async () => {
    if (isLiking) return;
    setIsLiking(true);
    try {
      await onLike(comment.id);
    } finally {
      setIsLiking(false);
    }
  };

  const maxLevel = 3; // Maximum nesting level
  const canReply = level < maxLevel;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`${level > 0 ? 'ml-8 border-l-2 border-gray-200 pl-4' : ''} mb-4`}
    >
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        {/* Comment Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {comment.authorName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h4 className="font-medium text-gray-900">{comment.authorName}</h4>
                {comment.isAuthorReply && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                    Author
                  </span>
                )}
                {comment.authorWebsite && (
                  <a
                    href={comment.authorWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Calendar size={14} />
                <span>{commentService.formatRelativeTime(comment.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Comment Content */}
        <div className="mb-4">
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {comment.content}
          </p>
        </div>

        {/* Comment Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLike}
              disabled={isLiking}
              className="flex items-center space-x-2 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              <Heart size={16} className={comment.likeCount > 0 ? 'fill-current text-red-600' : ''} />
              <span className="text-sm">{comment.likeCount || 0}</span>
            </button>

            {canReply && (
              <button
                onClick={() => onReply(comment.id)}
                className="flex items-center space-x-2 text-gray-500 hover:text-blue-600 transition-colors"
              >
                <Reply size={16} />
                <span className="text-sm">Reply</span>
              </button>
            )}
          </div>

          {comment.replies && comment.replies.length > 0 && (
            <button
              onClick={() => setShowReplies(!showReplies)}
              className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span className="text-sm">{comment.replies.length} repl{comment.replies.length === 1 ? 'y' : 'ies'}</span>
              {showReplies ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Replies */}
      <AnimatePresence>
        {showReplies && comment.replies && comment.replies.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4"
          >
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                onReply={onReply}
                onLike={onLike}
                level={level + 1}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CommentSection: React.FC<CommentSectionProps> = ({ contentType, contentId, title }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stats, setStats] = useState({ totalComments: 0, pendingComments: 0 });
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const [formData, setFormData] = useState<CommentFormData>({
    content: '',
    authorName: '',
    authorEmail: '',
    authorWebsite: ''
  });

  // Load comments and stats
  useEffect(() => {
    loadComments();
    loadStats();
  }, [contentType, contentId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await commentService.getComments(contentType, contentId);
      if (response.success && response.data) {
        setComments(response.data.comments);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await commentService.getCommentStats(contentType, contentId);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error: any) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.content.trim()) {
      setError('Comment content is required');
      return;
    }
    if (!formData.authorName.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.authorEmail.trim()) {
      setError('Email is required');
      return;
    }
    if (!commentService.isValidEmail(formData.authorEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    if (formData.authorWebsite && !commentService.isValidURL(formData.authorWebsite)) {
      setError('Please enter a valid website URL');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const commentData: CommentFormData = {
        content: commentService.formatCommentContent(formData.content),
        authorName: formData.authorName.trim(),
        authorEmail: formData.authorEmail.trim(),
        authorWebsite: formData.authorWebsite?.trim() || undefined,
        parentId: replyToId || undefined
      };

      const response = await commentService.createComment(contentType, contentId, commentData);
      
             if (response.success) {
         setSuccess(replyToId ? 'Reply posted successfully!' : 'Comment posted successfully!');
         setFormData({ content: '', authorName: '', authorEmail: '', authorWebsite: '' });
         setShowCommentForm(false);
         setReplyToId(null);
         
         // Invalidate cache for this content and reload
         commentService.invalidateCache(contentType, contentId);
         await loadComments();
         await loadStats();
         
         // Clear success message after 3 seconds
         setTimeout(() => setSuccess(null), 3000);
       }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = (parentId: number) => {
    setReplyToId(parentId);
    setShowCommentForm(true);
  };

  const handleLike = async (commentId: number) => {
    try {
      await commentService.likeComment(commentId);
      // Invalidate cache and reload comments to get updated like count
      commentService.invalidateCache(contentType, contentId);
      await loadComments();
    } catch (error: any) {
      console.error('Error liking comment:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate cache and force refresh
      commentService.invalidateCache(contentType, contentId);
      await loadComments();
      await loadStats();
    } catch (error: any) {
      console.error('Error refreshing comments:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleInputChange = (field: keyof CommentFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  return (
    <div className="mt-12 border-t border-gray-200 pt-8">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <MessageSquare size={24} className="text-blue-600" />
          <h3 className="text-2xl font-bold text-gray-900">
            Comments ({stats.totalComments})
          </h3>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh comments"
          >
            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
            <span className="text-sm">Refresh</span>
          </button>
          
          <button
            onClick={() => {
              setShowCommentForm(!showCommentForm);
              setReplyToId(null);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <MessageSquare size={16} />
            <span>Add Comment</span>
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3"
          >
            <AlertCircle size={20} className="text-red-600" />
            <p className="text-red-800">{error}</p>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
          >
            <AlertCircle size={20} className="text-green-600" />
            <p className="text-green-800">{success}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comment Form */}
      <AnimatePresence>
        {showCommentForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                {replyToId ? 'Reply to Comment' : 'Leave a Comment'}
              </h4>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                    Comment *
                  </label>
                  <textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    rows={4}
                    maxLength={2000}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Share your thoughts..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.content.length}/2000 characters
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="authorName" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="authorName"
                      value={formData.authorName}
                      onChange={(e) => handleInputChange('authorName', e.target.value)}
                      maxLength={100}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Your name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="authorEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="authorEmail"
                      value={formData.authorEmail}
                      onChange={(e) => handleInputChange('authorEmail', e.target.value)}
                      maxLength={255}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="your@email.com"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your email will not be published
                    </p>
                  </div>
                </div>

                <div>
                  <label htmlFor="authorWebsite" className="block text-sm font-medium text-gray-700 mb-2">
                    Website (optional)
                  </label>
                  <input
                    type="url"
                    id="authorWebsite"
                    value={formData.authorWebsite}
                    onChange={(e) => handleInputChange('authorWebsite', e.target.value)}
                    maxLength={255}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCommentForm(false);
                      setReplyToId(null);
                      setError(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={16} />
                    <span>{submitting ? 'Posting...' : 'Post Comment'}</span>
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comments List */}
      <div>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No comments yet. Be the first to share your thoughts!</p>
            <button
              onClick={() => setShowCommentForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add the first comment
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onReply={handleReply}
                onLike={handleLike}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
